import { useEffect, useState } from "react";
import { api } from "../../../../convex/_generated/api";
import { getConvexClient } from "@/lib/convex";
import type { ArcadeViewer, UnlockCheckResult } from "@/utils/arcade/playGate";
import type { BoardPeriod } from "@/utils/arcade/leaderboard";
import type { ArcadeGameKind } from "@/utils/arcade/games";
import type { SubscribeOutcome } from "@/utils/mailingUnlock";
import { createFakeArcadeBackend, fakeArcadeConfig } from "./fakeArcadeBackend";

// Everything the arcade asks Convex for, in one place, through the shared
// client rather than useQuery: a function missing on a stale deployment, or a
// backend that never answers, leaves the retention UI empty instead of
// throwing into the route. In dev a Playwright script can swap in the
// in-memory fake (window.__ARCADE_FAKE__), because the dev mocks cannot speak
// the Convex protocol.

export interface BoardArgs {
  songSlug: string;
  game: string;
  period: BoardPeriod;
  challenge?: string;
}

export interface BoardEntry {
  rank: number;
  nickname: string;
  score: number;
  level: number;
}

export interface MineArgs extends BoardArgs {
  email?: string;
  unlockToken?: string;
}

export interface MineResult {
  rank: number;
  rankCapped: boolean;
  score: number;
  nickname: string;
}

export interface SubmitArgs {
  songSlug: string;
  game: string;
  score: number;
  level: number;
  durationSec: number;
  nickname: string;
  challenge?: string;
  email?: string;
  unlockToken?: string;
  // The server's receipt for this run (arcadeScores.startRun).
  runToken?: string;
}

// review: the score is fine to keep but waits for the admin before it shows.
export type SubmitResult =
  | { posted: true; improved: boolean; best: number; rank: number; rankCapped: boolean; todayRank: number; challengeRank: number | null }
  | { posted: false; reason: string; review?: boolean };

export interface HighScoreEntry {
  rank: number;
  nickname: string;
  score: number;
  level: number;
  songSlug: string;
}

export interface HighScoresResult {
  games: Array<{ game: ArcadeGameKind | string; entries: HighScoreEntry[] }>;
  endless: HighScoreEntry[];
  legends: Array<{ rank: number; nickname: string; points: number; wins: number; games: number }>;
  players: number;
  updatedAt: number;
}

export interface SubscribeArgs {
  email: string;
  source: "arcade_gate";
  pagePath?: string;
  referrer?: string;
  visitorId?: string;
  country?: string;
  deviceType?: string;
  website?: string;
}

export interface ArcadeQueries {
  viewer: { args: Record<string, never>; result: ArcadeViewer };
  checkUnlock: { args: { email: string; token: string }; result: UnlockCheckResult };
  board: { args: BoardArgs; result: { entries: BoardEntry[] } };
  mine: { args: MineArgs; result: MineResult | null };
  highScores: { args: Record<string, never>; result: HighScoresResult | null };
}

export type ArcadeQueryName = keyof ArcadeQueries;

export interface ArcadeBackend {
  ready: boolean;
  watch<N extends ArcadeQueryName>(name: N, args: ArcadeQueries[N]["args"], onValue: (value: ArcadeQueries[N]["result"]) => void, onError: () => void): () => void;
  subscribe(args: SubscribeArgs): Promise<SubscribeOutcome>;
  requestUnlockLink(args: { email: string; pagePath?: string; visitorId?: string; deviceType?: string }): Promise<void>;
  submitScore(args: SubmitArgs): Promise<SubmitResult>;
  // Asks the server to sign the start of a run; null when it cannot.
  startRun(args: { songSlug: string; game: string }): Promise<string | null>;
}

const convexQueries = {
  viewer: api.viewer.me,
  checkUnlock: api.mailingList.checkUnlock,
  board: api.arcadeScores.board,
  mine: api.arcadeScores.mine,
  highScores: api.arcadeScores.highScores,
} as const;

function createConvexBackend(): ArcadeBackend {
  const client = getConvexClient();
  return {
    ready: Boolean(client),
    watch(name, args, onValue, onError) {
      if (!client) return () => undefined;
      const watch = client.watchQuery(convexQueries[name] as never, args as never);
      let failed = false;
      const read = () => {
        try {
          const value = watch.localQueryResult();
          if (value !== undefined) onValue(value as never);
        } catch (error) {
          if (!failed) console.warn(`[arcade] ${name} failed, keeping the fallback:`, error);
          failed = true;
          onError();
        }
      };
      read();
      return watch.onUpdate(read);
    },
    async subscribe(args) {
      if (!client) throw new Error("The mailing list is offline right now.");
      const result = await client.mutation(api.mailingList.subscribe, args);
      return { email: result.email, status: result.status, alreadySubscribed: result.alreadySubscribed, confirmationSent: result.confirmationSent };
    },
    async requestUnlockLink(args) {
      if (!client) return;
      await client.mutation(api.mailingList.requestUnlockLink, args);
    },
    async submitScore(args) {
      if (!client) return { posted: false, reason: "Scores cannot be posted right now." };
      return (await client.mutation(api.arcadeScores.submit, args)) as SubmitResult;
    },
    async startRun(args) {
      if (!client) return null;
      const result = await client.mutation(api.arcadeScores.startRun, args);
      return result?.token ?? null;
    },
  };
}

let backend: ArcadeBackend | null = null;

export function getArcadeBackend(): ArcadeBackend {
  if (backend) return backend;
  // Dev only: the browser playtests install a fake before the page loads.
  if (import.meta.env.DEV) {
    const config = fakeArcadeConfig();
    if (config) {
      backend = createFakeArcadeBackend(config);
      return backend;
    }
  }
  backend = createConvexBackend();
  return backend;
}

export type ArcadeQueryState<T> = { status: "loading" } | { status: "ready"; value: T } | { status: "error" };

export function useArcadeQuery<N extends ArcadeQueryName>(name: N, args: ArcadeQueries[N]["args"] | "skip"): ArcadeQueryState<ArcadeQueries[N]["result"]> {
  const key = args === "skip" ? "skip" : JSON.stringify(args);
  const [state, setState] = useState<{ key: string; result: ArcadeQueryState<ArcadeQueries[N]["result"]> }>({ key, result: { status: "loading" } });

  useEffect(() => {
    if (args === "skip") return;
    const source = getArcadeBackend();
    if (!source.ready) return;
    return source.watch(
      name,
      args,
      (value) => setState({ key, result: { status: "ready", value } }),
      () => setState((current) => (current.key === key && current.result.status === "error" ? current : { key, result: { status: "error" } })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, key]);

  if (args === "skip") return { status: "loading" };
  return state.key === key ? state.result : { status: "loading" };
}
